import { useState, type ReactElement } from 'react';
import { Group, SegmentedControl, Stack, Text } from '@mantine/core';
import type { TopicDefinition } from './TopicCatalogTypes';
import type { AdHocAggregateResult, AnalyticsMetric } from '@/shared/analytics/AnalyticsTypes';
import { sliceToPath } from './TopicSliceKey';
import { assistFieldLabel, formatCount, formatTokens, formatUsd } from '@/shared/analytics/MetricFormatting';
import HeatMap, { type HeatMapCell, type HeatMapColumn, type HeatMapRow } from '@/design/HeatMap/HeatMap';

interface MatrixVizProps {
    definition: TopicDefinition;
    primary: AdHocAggregateResult;
    matrix: AdHocAggregateResult;
    basePath: string;
    /** When set, shows the Prompts / Total tokens / Cost / Model fit view switcher above the grid. */
    showViews?: boolean;
}

type CellMetrics = Partial<Record<AnalyticsMetric, number>>;

/**
 * The views the matrix can switch between. Standard views colour and display the
 * same metric; `modelFit` is special — it colours each cell by cost (where the
 * money goes) while displaying the average fit score, with a `$` cost-tier cue.
 */
type MatrixView = 'turns' | 'totalTokens' | 'allocatedCostUsd' | 'modelFit';

const VIEW_OPTIONS: { value: MatrixView; label: string }[] = [
    { value: 'turns', label: 'Prompts' },
    { value: 'totalTokens', label: 'Total tokens' },
    { value: 'allocatedCostUsd', label: 'Cost' },
    { value: 'modelFit', label: 'Model fit' },
];

const NOUN: Record<MatrixView, string> = { turns: 'prompts', totalTokens: 'tokens', allocatedCostUsd: 'spend', modelFit: 'fit score' };

function formatView(view: MatrixView, value: number): string {
    if (view === 'turns') return formatCount(value);
    if (view === 'totalTokens') return formatTokens(value);
    if (view === 'allocatedCostUsd') return formatUsd(value);
    return `${Math.round(value)}`;
}

/** The value shown in a cell: the fit score for `modelFit`, otherwise the metric itself. */
function displayValue(view: MatrixView, m: CellMetrics): number {
    return (view === 'modelFit' ? m.avgModelFit : m[view]) ?? 0;
}

/** The value driving cell colour: cost for `modelFit`, otherwise the displayed metric. */
function colorValue(view: MatrixView, m: CellMetrics): number {
    return (view === 'modelFit' ? m.allocatedCostUsd : m[view]) ?? 0;
}

/**
 * Cross-tab heatmap. Turns two aggregate results (primary rows, secondary
 * columns) into the normalised {@link HeatMap} model: every cell drills into
 * the L2 evidence for that exact (row × column) slice, and the primary axis's
 * headers drill into that whole slice. `matrixOrientation: 'primary-cols'`
 * transposes the display (primary values as columns) without changing drill
 * semantics. The view switcher picks which metric each cell shows.
 */
export default function MatrixViz({ definition, primary, matrix, basePath, showViews = false }: MatrixVizProps): ReactElement {
    const [view, setView] = useState<MatrixView>('turns');
    const rowLimit = definition.matrixRowLimit ?? 8;
    const primaryRows = primary.rows.slice(0, rowLimit).map((row) => row.keys[definition.primaryField] ?? '—');
    const rowSet = new Set(primaryRows);

    // Full metrics per (primary, secondary) cell, so any view derives its display + colour from the same store.
    const byPrimary = new Map<string, Map<string, CellMetrics>>();
    const secColorTotal = new Map<string, number>();
    for (const row of matrix.rows) {
        const rowValue = row.keys[definition.primaryField] ?? '—';
        if (!rowSet.has(rowValue)) continue;
        const colValue = definition.secondaryField === undefined ? '—' : row.keys[definition.secondaryField] ?? '—';
        const inner = byPrimary.get(rowValue) ?? new Map<string, CellMetrics>();
        inner.set(colValue, row.metrics);
        byPrimary.set(rowValue, inner);
        secColorTotal.set(colValue, (secColorTotal.get(colValue) ?? 0) + colorValue(view, row.metrics));
    }
    const primaryCols = [...secColorTotal.entries()].sort((a, b) => b[1] - a[1]).map(([col]) => col);

    const transposed = definition.matrixOrientation === 'primary-cols';
    const rowValues = transposed ? primaryCols : primaryRows;
    const colValues = transposed ? primaryRows : primaryCols;
    const metricsOf = (rowValue: string, colValue: string): CellMetrics | undefined =>
        transposed ? byPrimary.get(colValue)?.get(rowValue) : byPrimary.get(rowValue)?.get(colValue);

    let maxColor = 0;
    for (const rowValue of rowValues) {
        for (const colValue of colValues) {
            const m = metricsOf(rowValue, colValue);
            if (m !== undefined) maxColor = Math.max(maxColor, colorValue(view, m));
        }
    }

    // Totals: a sum for the count/cost views, a turns-weighted average for the fit view.
    const totalOver = (cells: (CellMetrics | undefined)[]): number => {
        if (view === 'modelFit') {
            let weighted = 0;
            let turns = 0;
            for (const m of cells) {
                if (m === undefined) continue;
                weighted += (m.avgModelFit ?? 0) * (m.turns ?? 0);
                turns += m.turns ?? 0;
            }
            return turns > 0 ? weighted / turns : 0;
        }
        return cells.reduce((sum, m) => sum + (m === undefined ? 0 : displayValue(view, m)), 0);
    };
    const rowTotalOf = (rowValue: string): number => totalOver(colValues.map((colValue) => metricsOf(rowValue, colValue)));
    const colTotalOf = (colValue: string): number => totalOver(rowValues.map((rowValue) => metricsOf(rowValue, colValue)));

    const rows: HeatMapRow[] = rowValues.map((rowValue) => ({
        key: rowValue,
        label: rowValue,
        href: transposed ? undefined : sliceToPath(basePath, definition.id, { primary: rowValue }),
        total: rowTotalOf(rowValue),
    }));
    const columns: HeatMapColumn[] = colValues.map((colValue) => ({
        key: colValue,
        label: colValue,
        sublabel: formatView(view, colTotalOf(colValue)),
        href: transposed ? sliceToPath(basePath, definition.id, { primary: colValue }) : undefined,
    }));
    const cellAt = (rowKey: string, colKey: string): HeatMapCell => {
        const m = metricsOf(rowKey, colKey);
        const value = m === undefined ? 0 : displayValue(view, m);
        const color = m === undefined ? 0 : colorValue(view, m);
        const href = value === 0 ? undefined : sliceToPath(basePath, definition.id, transposed ? { primary: colKey, secondary: rowKey } : { primary: rowKey, secondary: colKey });
        return { value, colorValue: color, sublabel: view === 'modelFit' && color > 0 ? formatUsd(color) : undefined, href };
    };

    const cornerLabel = transposed && definition.secondaryField !== undefined ? assistFieldLabel(definition.secondaryField).toLowerCase() : definition.lensLabel;

    const heatMap = (
        <HeatMap
            rows={rows}
            columns={columns}
            cellAt={cellAt}
            maxValue={maxColor}
            maxColorValue={maxColor}
            accent={definition.accent}
            cornerLabel={cornerLabel}
            metricNoun={NOUN[view]}
            intensityNoun={view === 'modelFit' ? 'higher cost' : 'more'}
            formatValue={(value) => formatView(view, value)}
            columnMinWidth={transposed ? 84 : 64}
        />
    );
    if (!showViews) return heatMap;
    return (
        <Stack gap="sm">
            <Group justify="space-between" align="center" gap="sm">
                <SegmentedControl size="xs" value={view} onChange={(next) => setView(next as MatrixView)} w="fit-content" data={VIEW_OPTIONS} />
                <Text size="xs" c="dimmed" visibleFrom="sm">
                    {view === 'modelFit' ? 'Score = model fit · shading = cost' : 'Click any cell or row to see the prompts behind it →'}
                </Text>
            </Group>
            {heatMap}
        </Stack>
    );
}
